import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";

export interface ApiProject {
  id: string;
  brandId: string;
  title: string;
  brief: string;
  pipelineStage: string;
  targetPlatforms: string[];
  targetFormats: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AiReviewNotes {
  issues: string[];
  suggestions: string[];
  model?: string;
  autoApproved?: boolean;
}

export interface ApiPiece {
  id: string;
  shortCode: string;
  projectId: string;
  brandId: string;
  platform: string;
  format: string;
  postType?: string | null;
  copy: string;
  caption: string;
  hashtags: string[];
  mediaUrls: string[];
  thumbnailUrl: string | null;
  title?: string | null;
  description?: string | null;
  threadItems?: Array<{ content: string }> | null;
  platformKnobs?: Record<string, any>;
  status: string;
  version: number;
  requiresReapproval: boolean;
  /** When non-null, the Piece has an in-flight MediaConvert job for its
   *  video derivative. Approve / schedule are blocked until this clears. */
  transcodeJobId?: string | null;
  rejectionReason: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  aiReviewScore?: number | null;
  aiReviewNotes?: AiReviewNotes | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetail extends ApiProject {
  pieces: ApiPiece[];
}

export function useProjects(brandId: string, stage?: string) {
  return useQuery({
    queryKey: ["projects", brandId, stage],
    queryFn: async () => {
      const url = stage
        ? `/brands/${brandId}/projects?pipelineStage=${stage}`
        : `/brands/${brandId}/projects`;
      return apiFetch<ApiProject[]>(url);
    },
    enabled: !!brandId,
  });
}

export function useProject(brandId: string, projectId: string) {
  return useQuery({
    queryKey: ["project", brandId, projectId],
    queryFn: () => apiFetch<ProjectDetail>(`/brands/${brandId}/projects/${projectId}`),
    enabled: !!brandId && !!projectId,
  });
}

export type ProjectPatch = Partial<
  Pick<ApiProject, "title" | "brief" | "targetPlatforms" | "targetFormats" | "pipelineStage">
>;

/**
 * PATCH a project with an optimistic cache update (house pattern:
 * use-send-inbox-message — cancel, snapshot, set, rollback on error).
 */
export function useUpdateProject(brandId: string, projectId: string) {
  const qc = useQueryClient();
  const queryKey = ["project", brandId, projectId];
  return useMutation({
    mutationFn: (patch: ProjectPatch) =>
      apiFetch<ApiProject>(`/brands/${brandId}/projects/${projectId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<ProjectDetail>(queryKey);
      if (previous) {
        qc.setQueryData<ProjectDetail>(queryKey, { ...previous, ...patch });
      }
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(queryKey, ctx.previous);
      }
      toast.error("Failed to update project");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["projects", brandId] });
    },
  });
}
